<template>
  <section v-if="hasRatesContent">
    <THeading
      :level="3"
      tag="h3"
      class="mb-4"
    >
      {{ t('title') }}
    </THeading>

    <TGrid class="mt-2">
      <RvRentalDetailItem
        v-if="rvSecurityDeposit && rvSecurityDeposit > 0"
        :label="t('securityDeposit.text')"
        class="mb-4"
      >
        <span class="font-semibold">{{ formatPrice({ value: rvSecurityDeposit }) }}</span>
        <TTooltipIcon
          :content="t('securityDeposit.tooltip')"
          :title="t('securityDeposit.text')"
          class="ml-2 inline-block"
          icon-variant="muted"
        />
        <div>{{ t('fullyRefundable') }}</div>
      </RvRentalDetailItem>

      <RvRentalDetailItem
        v-if="rvIsMotorized"
        :label="t('mileage')"
        class="mb-4"
      >
        <template v-if="rvDailyKMAllowed && rvDailyKMAllowed > 0">
          <span class="font-semibold">{{ t('dailyMileageAllowed', { amount: rvDailyKMAllowed, unit: rvMeasurementUnit }) }}</span>/<span class="lowercase">{{ t('day-free') }}</span>
          <div>
            {{ formatPrice({ value: rvSurchargePerExtraKM, round: false }) }}/{{ t('surchargePerExtraMileage', { unit: rvMeasurementUnit }) }}
          </div>
        </template>
        <template v-else-if="rvIsChargeMileageEnabled">
          <span class="font-semibold">{{ formatPrice({ value: rvSurchargePerExtraKM, round: false }) }}</span>/{{ t('unit', { unit: rvMeasurementUnit }) }}
        </template>
        <template v-else>
          <span class="font-semibold">{{ t('unlimited') }}</span>
        </template>
      </RvRentalDetailItem>
      <RvRentalDetailItem
        v-if="rvIsGenerator"
        :label="t('powerGenerator')"
        class="mb-4"
      >
        <template v-if="rvFreeHoursPerDay && rvFreeHoursPerDay > 0">
          <span class="font-semibold">{{ t('FreeHoursPerDay', { amount: rvFreeHoursPerDay }) }}</span>/<span class="lowercase">{{ t('day-free') }}</span>
          <div>{{ formatPrice({ value: rvChargePerHourOver, round: false }) }}/{{ t('ChargePerHourOver') }}</div>
        </template>
        <template v-else-if="rvIsChargeGeneratorEnabled">
          <span class="font-semibold">{{ formatPrice({ value: rvChargePerHourOver, round: false }) }}</span>/{{ t('hour') }}
        </template>
        <template v-else>
          <span class="font-semibold">{{ t('unlimited') }}</span>
        </template>
      </RvRentalDetailItem>
    </TGrid>
  </section>
</template>

<script setup lang="ts">
import type { Nullable } from '~/types'

const { priceDisplay } = usePrice()
const { t } = useI18n()

const {
  rvChargePerHourOver,
  rvCountry,
  rvDailyKMAllowed,
  rvFreeHoursPerDay,
  rvIsGenerator,
  rvIsChargeGeneratorEnabled,
  rvIsChargeMileageEnabled,
  rvMeasurementUnit,
  rvSecurityDeposit,
  rvSurchargePerExtraKM,
  rvIsMotorized,
} = useRvDetails()

const hasRatesContent = computed(() => {
  return rvSecurityDeposit.value !== null || rvIsMotorized.value || rvIsGenerator.value
})

function formatPrice({ value, round = true }: { value?: Nullable<number>, round?: boolean }) {
  return priceDisplay({
    value,
    round,
    countryCode: rvCountry.value,
  })
}
</script>

<i18n lang="json">
{
  "en": {
    "title": "Rates",
    "night": "Night",
    "mileage": "Mileage",
    "dailyMileageAllowed": "{amount}{unit}",
    "day-free": "day free",
    "surchargePerExtraMileage": "{unit} extra",
    "unlimited": "Unlimited",
    "powerGenerator": "Power Generator",
    "FreeHoursPerDay": "{amount} hours",
    "ChargePerHourOver": "hour extra",
    "hour": "hour",
    "unit": "{unit}",
    "fullyRefundable": "Fully refundable",
    "percentDiscount": "{amount}% off",
    "securityDeposit": {
      "text": "Security Deposit",
      "tooltip": "This deposit is taken 72hrs prior to the rental date and held during the duration of the trip and is fully refundable."
    }
  },
  "fr": {
    "title": "Tarifs",
    "night": "Nuit",
    "mileage": "Kilométrage",
    "dailyMileageAllowed": "{amount}{unit}",
    "day-free": "jour gratuit",
    "surchargePerExtraMileage": "{unit} en plus",
    "unlimited": "Illimité",
    "powerGenerator": "Génératrice",
    "FreeHoursPerDay": "{amount} heures",
    "ChargePerHourOver": "heure en plus",
    "hour": "heure",
    "unit": "{unit}",
    "fullyRefundable": "Remboursement total",
    "percentDiscount": "{amount}% rabais",
    "securityDeposit": {
      "text": "Dépôt de sécurité",
      "tooltip": "Ce dépôt est pris 72h avant la location date et tenue pendant la durée du voyage et est entièrement remboursable."
    }
  }
}
</i18n>
